import * as React from "react";
import { useTranslation } from "react-i18next";
import { DashboardController } from "../../../controllers";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import ResourceTable from "../../Resource/ResourceTable";
import ResourceTableRow from "../../Resource/ResourceTableRow";
import ResourceTableRowData from "../../Resource/ResourceTableRowData";
import styled from "styled-components";
import TableWidgetWrapper from "./TableWidgetWrapper";
import NumberFormatter from "../../../utilities/NumberFormatter";
import ButtonGroup from "../../Button/ButtonGroup";

const Container = styled.div`
  height: 100%;
`

const ButtonGroupContainer = styled.div`
  > div {
    min-width: 200px;
  }
`

interface QuarterDataRow {
  label: string
  years: number[]
}

interface IProps {
  currency: string;
  numberFormat: string;
}

const QuarterWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [mode, setMode] = React.useState<'net' | 'vat'>('net');
  const [response, setResponse] = React.useState<{ name: string; quarters: { net: number; vat: number }[] }[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getQuarterSummary();
      setResponse(response);
    } catch (error) {
      console.error("Failed to fetch quarter summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={t('QuarterWidget::Quarters')}>
        <PageLoader />
      </Panel>
    );
  }

  const onModeChange = (mode: 'net' | 'vat') => {
    setMode(mode);
  }

  const years = response.map((fy) => fy.name)
  const data: QuarterDataRow[] = [0, 1, 2, 3].map((qi) => ({
    label: t('QuarterWidget::Quarter {{number}}', { number: qi + 1 }),
    years: response.map((fy) => mode === 'net' ? fy.quarters[qi]?.net ?? 0 : fy.quarters[qi]?.vat ?? 0),
  }));

  return (
    <Container>
      <Panel
        className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto"
        title={t('QuarterWidget::Quarters')}
        headerRight={
          <ButtonGroupContainer>
            <ButtonGroup
              items={[
                { element: t('QuarterWidget::Total excl.'), active: mode === 'net', onClick: () => onModeChange('net') },
                { element: t('QuarterWidget::VAT'), active: mode === 'vat', onClick: () => onModeChange('vat') }
              ]}
            />
          </ButtonGroupContainer>
        }
      >
        <TableWidgetWrapper>
          <ResourceTable
            data={data}
            headers={[
              { title: t('QuarterWidget::Cash flow') },
              ...years.map(name => ({ title: name }))
            ]}
            renderRow={(item: QuarterDataRow) => {
              return (
                <ResourceTableRow>
                  <ResourceTableRowData>
                    {item.label}
                  </ResourceTableRowData>
                  {item?.years?.map(year => (
                    <ResourceTableRowData>
                      {NumberFormatter.formatCurrency(currency, numberFormat, year)}
                    </ResourceTableRowData>
                  ))}
                </ResourceTableRow>
              )
            }}
          />
        </TableWidgetWrapper>
      </Panel>
    </Container>
  )
}

export default QuarterWidget
